import React, { useState } from 'react';
import {
    Modal,
    Button,
    Box,
    SpaceBetween,
    Container,
    Spinner,
    Alert,
} from '@amzn/awsui-components-react-v3';
import FullCalendar from '@fullcalendar/react';
import CalendarResourceView from '../../../Common/ResourceCalendar/CalendarView';
import {
    InstructorCalendarHeaderPropsData,
    AssignInstructorModalPropsData,
    CalendarViewPropsData,
} from '../../../../interfaces/resourceCalendarProps';
import { RecommendedInstructorProfileData } from '../../../../interfaces/recommendedInstructorProfile';
import CalendarHeader from '../../../Common/ResourceCalendar/Header';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';

import ValidateBackToBackActivitiesModal from '../../../Common/ValidationModals/ValidateBackToBackActivitiesModal';
import ValidateReassignInstructorModal from '../../../Common/ValidationModals/ValidateReassignInstructorModal';
import ValidateInstructorBusyModal from '../../../Common/ValidationModals/ValidateInstructorBusyModal';
import ValidateInstructorQualificationsModal from '../../../Common/ValidationModals/ValidateInstructorQualificationsModal';

import './AssignInstructorModal.scss';
import SchedulingAssistant from './SchedulingAssistant';
import {
    validateInstructorActivityQualifications,
    validateInstructorsForActivity,
} from '../../../../services/activity-service';
import {
    InstructorAssignmentConflict,
    InstructorAvailabilityData,
} from '../../Common/Common';
import { ActivityData } from '../../../../interfaces/activity';
import { formatUnixTimeInTimezone } from '../../../../../common/utils/date-time.utils';
import { useSelector } from 'react-redux';
import { selectCount } from '../../../../store/slices/recommendedInstructorsSlice';

const ASSIGN_INSTRUCTOR_TEXT = 'Assign instructor';

enum WarningType {
    Busy = 'busy',
    Reassign = 'reassign',
    BackToBack = 'back_to_back',
    Qualifications = 'qualifications',
}

interface PendingWarning {
    type: WarningType;
    instructor: InstructorAvailabilityData;
    activity: ActivityData | null;
    missingQualifications?: Array<string>;
}

const AssignInstructorModal = ({
    isVisible,
    handleIsVisibleChange,
    deliverySessions,
    activityTimezone,
    handleAssignInstructorEvent,
    handleQueryChange,
    filteringOptions,
    instructorFilteringProperties,
    query,
    areRecommendedInstructorsLoading,
    initialDate,
    handleFieldEvent,
    formValues,
    paginationProps,
    handlePreferencesChange,
    ...assignInstructorProps
}: AssignInstructorModalPropsData) => {
    const [calendarRef, setCalendarRef] = useState<FullCalendar>(
        {} as FullCalendar,
    );
    const [reassignReason, setReassignReason] =
        useState<OptionDefinition | null>(null);
    const [selectedInstructors, setSelectedInstructors] = useState(
        new Set<RecommendedInstructorProfileData>(),
    );
    const [pendingWarnings, setPendingWarnings] = useState<PendingWarning[]>(
        [],
    );
    const [currentWarningIndex, setCurrentWarningIndex] = useState<
        number | null
    >(null);
    const [validationError, setValidationError] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);

    const handleSelectedInstructorsChange = (
        instructors: Set<RecommendedInstructorProfileData>,
    ) => {
        setSelectedInstructors(new Set(instructors));
    };
    const count = useSelector(selectCount);

    const currentWarning =
        currentWarningIndex !== null
            ? pendingWarnings[currentWarningIndex]
            : null;

    const instructorConflict: InstructorAssignmentConflict | null =
        currentWarning
            ? {
                  instructor: currentWarning.instructor,
                  activity: currentWarning.activity,
              }
            : null;

    const assignInstructorResourceViewProps: CalendarViewPropsData = {
        handleCalendarRefChange: (calendarRef: FullCalendar) =>
            setCalendarRef(calendarRef),
        selectedInstructors,
        handleSelectedInstructorsChange,
        deliverySessions,
        ...assignInstructorProps,
        initialDate,
        resourceLabelType: 'radio',
        calendarHeight: 500,
        courseName: formValues.course_name,
    };

    const handleConfirmAssignment = () => {
        handleAssignInstructorEvent(Array.from(selectedInstructors));
        if (reassignReason) {
            handleFieldEvent({
                instructor_assignment_override_reason: reassignReason.label,
            });
        }
        setSelectedInstructors(new Set<RecommendedInstructorProfileData>());
        handleIsVisibleChange(false);
        setPendingWarnings([]);
        setCurrentWarningIndex(null);
        setValidationError(false);
    };

    const advanceWarning = () => {
        if (
            currentWarningIndex !== null &&
            currentWarningIndex < pendingWarnings.length - 1
        ) {
            setCurrentWarningIndex(currentWarningIndex + 1);
        } else {
            setPendingWarnings([]);
            setCurrentWarningIndex(null);
            handleConfirmAssignment();
        }
    };

    const dismissWarning = () => {
        setPendingWarnings([]);
        setCurrentWarningIndex(null);
        setReassignReason(null);
    };

    const validateAssignInstructor = async () => {
        setValidationError(false);
        setIsValidating(true);
        const instructors = Array.from(selectedInstructors);
        for (let instructor of instructors) {
            if (!instructor) continue;
            const { pk: instructorPk } = instructor;
            try {
                let validationResponse = await validateInstructorsForActivity({
                    instructors: [
                        {
                            pk: instructorPk,
                            name: null,
                            email: null,
                            role: null,
                            type: null,
                            location: null,
                            do_not_shuffle: null,
                        },
                    ],
                    delivery_sessions: deliverySessions,
                    id: formValues.pk,
                    delivery_country: formValues.delivery_country,
                    delivery_language: formValues.delivery_language,
                    course_name: formValues.course_name,
                    delivery_timezone: formValues.delivery_timezone,
                    auto_assign_instructor: formValues.auto_assign_instructor,
                });

                const warnings: PendingWarning[] = [];

                for (let instructorValidationResponse of validationResponse) {
                    const {
                        availability,
                        back_to_back_activities,
                        booked_activities,
                    } = instructorValidationResponse;

                    if (['do_not_shuffle', 'reserved'].includes(availability)) {
                        warnings.push({
                            type: WarningType.Busy,
                            instructor: instructorValidationResponse,
                            activity:
                                booked_activities.length > 0
                                    ? booked_activities[0]
                                    : null,
                        });
                    }

                    if (booked_activities.length > 0) {
                        warnings.push({
                            type: WarningType.Reassign,
                            instructor: instructorValidationResponse,
                            activity: booked_activities[0],
                        });
                    }

                    if (back_to_back_activities.length > 0) {
                        warnings.push({
                            type: WarningType.BackToBack,
                            instructor: instructorValidationResponse,
                            activity: back_to_back_activities[0],
                        });
                    }
                }

                // Check qualifications
                const { missingQualifications, instructor: qualInstructor } =
                    validateInstructorActivityQualifications(
                        validationResponse,
                    );

                if (missingQualifications.length > 0 && qualInstructor) {
                    warnings.push({
                        type: WarningType.Qualifications,
                        instructor: qualInstructor,
                        activity: null,
                        missingQualifications,
                    });
                }

                if (warnings.length > 0) {
                    setPendingWarnings(warnings);
                    setCurrentWarningIndex(0);
                    setIsValidating(false);
                    return;
                }
            } catch (e) {
                setValidationError(true);
                setIsValidating(false);
                return;
            }
        }

        setIsValidating(false);
        handleConfirmAssignment();
    };

    const assignInstructorHeaderProps: InstructorCalendarHeaderPropsData = {
        calendarRef,
        handleQueryChange,
        filteringOptions,
        instructorFilteringProperties,
        query,
        instructorCountText: `${count} match${count > 1 ? 'es' : ''}`,
        filteringPlaceholder: 'Search',
        paginationProps,
        count,
        handlePreferencesChange,
    };

    return (
        <>
            <Modal
                className="assign-instructor-modal"
                onDismiss={() => handleIsVisibleChange(false)}
                visible={isVisible}
                closeAriaLabel="Close modal"
                size="max"
                data-testid={`AssignInstructorModal`}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button
                                variant="link"
                                onClick={() => handleIsVisibleChange(false)}
                                data-testid={`AssignInstructorCancel`}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                data-testid={`AssignInstructorConfirm`}
                                loading={isValidating}
                                onClick={() => {
                                    validateAssignInstructor();
                                }}
                            >
                                {ASSIGN_INSTRUCTOR_TEXT}
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
                header={ASSIGN_INSTRUCTOR_TEXT}
            >
                {isVisible && (
                    <SchedulingAssistant
                        overviewRequest={{
                            activityId: formValues.pk || undefined,
                        }}
                    />
                )}
                <Box variant="p">
                    Select an instructor from the list below to assign them to
                    this activity
                </Box>
                <Box
                    color="text-status-inactive"
                    fontSize="body-m"
                    variant="div"
                    margin={{ bottom: 'm' }}
                >
                    <SpaceBetween direction="horizontal" size="xl">
                        <span
                            data-testid={`AssignInstructorModalDeliverySessionDetails`}
                        >
                            Session date(s):{' '}
                            {deliverySessions
                                .filter((session) => {
                                    return (
                                        session.start_timestamp &&
                                        session.end_timestamp
                                    );
                                })
                                .map((session) =>
                                    formatUnixTimeInTimezone({
                                        timestamp:
                                            session.start_timestamp as number,
                                        timezone: activityTimezone,
                                        format: 'MMM D',
                                    }),
                                )
                                .join(', ')}
                        </span>
                        <span
                            data-testid={`AssignInstructorModalActivityTimezone`}
                        >
                            Timezone: {activityTimezone}
                        </span>
                    </SpaceBetween>
                </Box>
                <Container
                    disableContentPaddings
                    header={<CalendarHeader {...assignInstructorHeaderProps} />}
                >
                    {((loading: boolean) => {
                        if (loading) {
                            return (
                                <Box
                                    textAlign="center"
                                    padding={{ vertical: 'l' }}
                                >
                                    <Spinner data-testid="AssignInstructorModalSpinner" />
                                </Box>
                            );
                        } else if (!assignInstructorProps.resources.length) {
                            return (
                                <Box
                                    data-testid={`AssignInstructorModalEmptyState`}
                                    textAlign="center"
                                    padding={{ vertical: 'l' }}
                                >
                                    <Box
                                        color="text-status-inactive"
                                        fontSize="body-m"
                                        variant="strong"
                                        display="block"
                                        margin={{ bottom: 'xs' }}
                                    >
                                        Sorry, we didn't find any results.
                                    </Box>
                                    <Box
                                        color="text-status-inactive"
                                        margin={{ bottom: 's' }}
                                        fontSize="body-m"
                                    >
                                        Use fewer or different filters.
                                    </Box>
                                    <Button
                                        data-testid={`AssignInstructorModalClearFiltersButton`}
                                        variant="normal"
                                        onClick={() => {
                                            handleQueryChange({
                                                tokens: [],
                                                operation: 'and',
                                            });
                                        }}
                                    >
                                        Clear filters
                                    </Button>
                                </Box>
                            );
                        } else {
                            return (
                                <CalendarResourceView
                                    {...assignInstructorResourceViewProps}
                                />
                            );
                        }
                    })(areRecommendedInstructorsLoading)}
                </Container>
            </Modal>
            <ValidateBackToBackActivitiesModal
                instructorConflict={instructorConflict}
                visible={currentWarning?.type === WarningType.BackToBack}
                onCancel={dismissWarning}
                onConfirm={advanceWarning}
            />
            <ValidateReassignInstructorModal
                instructorConflict={instructorConflict}
                visible={currentWarning?.type === WarningType.Reassign}
                onCancel={dismissWarning}
                onConfirm={advanceWarning}
                reassignReason={reassignReason}
                setReassignReason={setReassignReason}
            />
            <ValidateInstructorBusyModal
                instructorConflict={instructorConflict}
                onCancel={dismissWarning}
                onConfirm={advanceWarning}
                visible={currentWarning?.type === WarningType.Busy}
                deliverySessions={deliverySessions}
                activityTimezone={activityTimezone}
            />
            <ValidateInstructorQualificationsModal
                instructorConflict={instructorConflict}
                onCancel={dismissWarning}
                visible={currentWarning?.type === WarningType.Qualifications}
                missingQualifications={
                    currentWarning?.missingQualifications || null
                }
                onConfirm={advanceWarning}
                formValues={formValues}
            />
            <Modal
                visible={validationError}
                onDismiss={() => setValidationError(false)}
                closeAriaLabel="Close modal"
                size="medium"
                data-testid="ValidationErrorModal"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button
                                variant="link"
                                onClick={() => setValidationError(false)}
                                data-testid="ValidationErrorCancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                data-testid="ValidationErrorConfirm"
                                onClick={() => {
                                    setValidationError(false);
                                    handleConfirmAssignment();
                                }}
                            >
                                Assign anyway
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
                header="Unable to verify availability"
            >
                <Alert type="warning">
                    Could not verify instructor availability. You may proceed,
                    but scheduling conflicts may exist.
                </Alert>
            </Modal>
        </>
    );
};

export default AssignInstructorModal;
